// La lupa del visor de escritorio — ADR-2026-10-06-la-lupa-es-un-circulo-sobre-la-imagen.
//
// Es el `Lens` de Aceternity que pasó Matías, portado sin `motion` (no está en el
// proyecto): un círculo que amplía la imagen EN EL LUGAR del cursor, no un cuadro aparte al
// costado. Una capa encima de la `<img>`, del mismo tamaño, repite la imagen escalada
// (`zoom`) con el origen en el cursor y una máscara radial que sólo deja ver el círculo.
// Entra y sale con un fundido y una escala corta (0,58 → 1, como el original).
//
// Escucha los eventos de la `<img>` (por `imgRef`) y mueve la capa escribiendo estilos
// directo, sin estado de React por movimiento: el visor que la monta es enorme y con un
// `setState` por `mousemove` se redibujaba entero en cada píxel.

import { useEffect, useRef, useState, type RefObject } from 'react';

export function LupaDeImagen({
  imgRef,
  src,
  habilitada,
  zoom = 2,
  tamano = 200,
  zIndex,
}: {
  /** La imagen del visor (la que se ve). */
  imgRef: RefObject<HTMLImageElement>;
  /** La MISMA url que muestra la imagen: con la grande bajando, amplía la miniatura.
   *  Sin url no hay lupa. */
  src: string | null | undefined;
  /** Apagada con un video, mientras carga o con un editor abierto encima. */
  habilitada: boolean;
  /** Cuánto amplía. */
  zoom?: number;
  /** Diámetro del círculo, en px. */
  tamano?: number;
  /** Para montarla sobre un overlay propio (el `z-40` de siempre quedaría abajo). */
  zIndex?: number;
}) {
  const [visible, setVisible] = useState(false);
  const capaRef = useRef<HTMLDivElement | null>(null);
  const lenteRef = useRef<HTMLDivElement | null>(null);
  const ampliadaRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img || !habilitada || !src) {
      setVisible(false);
      return;
    }
    let pendiente = 0;
    let ultimo: { x: number; y: number } | null = null;

    const pintar = () => {
      pendiente = 0;
      const capa = capaRef.current;
      const lente = lenteRef.current;
      const ampliada = ampliadaRef.current;
      if (!capa || !lente || !ampliada || !ultimo) return;
      const r = img.getBoundingClientRect();
      capa.style.left = `${r.left}px`;
      capa.style.top = `${r.top}px`;
      capa.style.width = `${r.width}px`;
      capa.style.height = `${r.height}px`;
      const x = ultimo.x - r.left;
      const y = ultimo.y - r.top;
      const mascara = `radial-gradient(circle ${tamano / 2}px at ${x}px ${y}px, black 100%, transparent 100%)`;
      lente.style.maskImage = mascara;
      lente.style.setProperty('-webkit-mask-image', mascara);
      lente.style.transformOrigin = `${x}px ${y}px`;
      ampliada.style.transformOrigin = `${x}px ${y}px`;
    };
    const mover = (e: MouseEvent) => {
      ultimo = { x: e.clientX, y: e.clientY };
      if (!pendiente) pendiente = window.requestAnimationFrame(pintar);
      // Si se cambió de imagen con el mouse encima, no hubo `mouseenter`: entra acá.
      setVisible(true);
    };
    const salir = () => setVisible(false);

    img.addEventListener('mouseenter', mover);
    img.addEventListener('mousemove', mover);
    img.addEventListener('mouseleave', salir);
    return () => {
      img.removeEventListener('mouseenter', mover);
      img.removeEventListener('mousemove', mover);
      img.removeEventListener('mouseleave', salir);
      if (pendiente) window.cancelAnimationFrame(pendiente);
      setVisible(false);
    };
  }, [imgRef, habilitada, src, tamano]);

  if (!habilitada || !src) return null;
  return (
    <div
      ref={capaRef}
      aria-hidden="true"
      className={`pointer-events-none fixed overflow-hidden rounded-lg ${zIndex === undefined ? 'z-40' : ''}`}
      style={zIndex === undefined ? undefined : { zIndex }}
    >
      <div
        ref={lenteRef}
        className={`absolute inset-0 overflow-hidden transition-[opacity,transform] duration-300 ease-out ${
          visible ? 'scale-100 opacity-100' : 'scale-[0.58] opacity-0'
        }`}
      >
        <img
          ref={ampliadaRef}
          src={src}
          alt=""
          draggable={false}
          className="absolute inset-0 h-full w-full select-none object-contain"
          style={{ transform: `scale(${zoom})` }}
        />
      </div>
    </div>
  );
}

export default LupaDeImagen;
